import { useAuth } from "@/contexts/AuthContext";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { ProductUpliftCard } from "@/components/dashboard/ProductUpliftCard";
// import { SalesVolumeCard } from "@/components/dashboard/SalesVolumeCard";
import { ProductPricesCard } from "@/components/dashboard/ProductPricesCard";
import { Package, TrendingUp, DollarSign, Target } from "lucide-react";
import { OptimizedProductsCard } from "@/components/dashboard/OptimizedProductsCard";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { calculateProfitUplift } from "@/lib/utils";
function Dashboard() {
    const { session, isDemoMode } = useAuth();
    const [metrics, setMetrics] = useState({
        productsOptimizing: { value: "0", trend: "0%", isPositive: true },
        averageUplift: { value: "0%", trend: "0%", isPositive: true },
        revenueGain: { value: "$0", trend: "0%", isPositive: true },
        activeOptimizations: { value: "0", trend: "0", isPositive: true },
    });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        async function fetchMetrics() {
            const { data, error } = await supabase
                .from("products")
                .select("*")
                .eq("user_id", session?.user.id)
                .in("status", ["Optimizing", "Optimized"]);

            if (data) {
                const optimizingCount = data.filter(
                    (p) => p.status === "Optimizing"
                ).length;
                const previousCount = optimizingCount - 1; // Simple mock trend

                const productsWithUplift = data.filter((product) => {
                    const hasUplift =
                        product.optimization_progress?.marketsets?.some(
                            (marketset: any) => {
                                const uplift =
                                    calculateProfitUplift(marketset.data) *
                                    100 *
                                    (isDemoMode ? 3 : 1);
                                console.log("Marketset uplift:", uplift);
                                return uplift > 0;
                            }
                        );
                    return hasUplift;
                });

                console.log("Products with uplift:", productsWithUplift.length);

                const averageUplift =
                    productsWithUplift.length > 0
                        ? productsWithUplift.reduce((acc, product) => {
                              const uplift =
                                  product.optimization_progress?.marketsets?.reduce(
                                      (max: number, marketset: any) => {
                                          const marketsetUplift =
                                              calculateProfitUplift(
                                                  marketset.data
                                              ) *
                                              100 *
                                              (isDemoMode ? 3 : 1);

                                          return Math.max(max, marketsetUplift);
                                      },
                                      0
                                  ) || 0;
                              return acc + uplift;
                          }, 0) / productsWithUplift.length
                        : 0;

                // Annual revenue base to apply uplift to - only used in demo mode
                const annualRevenue = isDemoMode ? 1278000 : 0; // £1.278 million in demo mode

                // Calculate revenue gain based on average uplift percentage
                const totalRevenue = isDemoMode
                    ? (averageUplift / 100) * annualRevenue
                    : 0;

                setMetrics({
                    productsOptimizing: {
                        value: optimizingCount.toString(),
                        trend: `${
                            previousCount > optimizingCount ? "-" : "+"
                        }1`,
                        isPositive: previousCount <= optimizingCount,
                    },
                    averageUplift: {
                        value: `${averageUplift.toFixed(1)}%`,
                        trend: "+2.1%", // Mock trend for now
                        isPositive: averageUplift > 0,
                    },
                    revenueGain: {
                        value:
                            totalRevenue === 0
                                ? "-"
                                : `£${totalRevenue.toLocaleString("en-GB", {
                                      maximumFractionDigits: 0,
                                  })}`,
                        trend: "+5.3%", // Mock trend for now
                        isPositive: totalRevenue > 0,
                    },
                    activeOptimizations: {
                        value: data
                            .filter((p) => !p.is_converged)
                            .length.toString(),
                        trend: "+3", // Mock trend for now
                        isPositive: true,
                    },
                });
            }
            setIsLoading(false);
        }

        fetchMetrics();
    }, [session]);

    return (
        <div className="w-full space-y-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <MetricCard
                    title="Products Optimizing"
                    value={metrics.productsOptimizing.value}
                    trend={metrics.productsOptimizing.trend}
                    isPositive={metrics.productsOptimizing.isPositive}
                    icon={Package}
                    comparisonText="from last month"
                />
                <MetricCard
                    title="Average Profit Uplift"
                    value={metrics.averageUplift.value}
                    trend={metrics.averageUplift.trend}
                    isPositive={metrics.averageUplift.isPositive}
                    icon={TrendingUp}
                    comparisonText="vs baseline"
                />
                <MetricCard
                    title="Annualized Profit Gain"
                    value={metrics.revenueGain.value}
                    trend={metrics.revenueGain.trend}
                    isPositive={metrics.revenueGain.isPositive}
                    icon={DollarSign}
                    comparisonText="from last week"
                />
                <MetricCard
                    title="Active Optimizations"
                    value={metrics.activeOptimizations.value}
                    trend={metrics.activeOptimizations.trend}
                    isPositive={metrics.activeOptimizations.isPositive}
                    icon={Target}
                    comparisonText="from yesterday"
                />
            </div>
            <OptimizedProductsCard />
            <div className="grid gap-6 md:grid-cols-2">
                <ProductUpliftCard />
                {/* <SalesVolumeCard /> */}
                <ProductPricesCard />
            </div>
        </div>
    );
}

export default Dashboard;
